import { useState } from 'react';

export type ServiceStatus = 'healthy' | 'degraded' | 'down';

export type ServiceEntry = {
  name: string;
  status: ServiceStatus;
  latencyMs: number;
};

export type ServiceStatusTrayProps = {
  services: ServiceEntry[];
};

const DOT_CLASS: Record<ServiceStatus, string> = {
  healthy: 'bg-healthy',
  degraded: 'bg-warn',
  down: 'bg-critical',
};

const STATUS_LABEL: Record<ServiceStatus, string> = {
  healthy: 'healthy',
  degraded: 'degraded',
  down: 'down',
};

/**
 * Row of small dots inside AppHeader — one per backend service. Each dot
 * is a real button (aria-label exposes the service name, status, and
 * latency); clicking the row opens a popover with the full breakdown.
 */
export function ServiceStatusTray({ services }: ServiceStatusTrayProps) {
  const [open, setOpen] = useState(false);
  const aggregate = services.some((s) => s.status === 'down')
    ? 'down'
    : services.some((s) => s.status === 'degraded')
      ? 'degraded'
      : 'healthy';

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Service status: ${STATUS_LABEL[aggregate]}, ${services.length} services`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-sp-1 rounded-r-chip px-sp-1 py-sp-1 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
      >
        {services.map((svc) => (
          <span
            key={svc.name}
            aria-label={`${svc.name}: ${STATUS_LABEL[svc.status]} (latency ${svc.latencyMs}ms)`}
            className={`inline-block h-sp-2 w-sp-2 rounded-r-chip ${DOT_CLASS[svc.status]}`}
          />
        ))}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Backend service status"
          className="absolute right-0 top-full z-20 mt-sp-2 w-72 rounded-r-card border border-gray-300 bg-gray-50 p-sp-3 shadow-md"
        >
          <p className="mb-sp-2 text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
            Service status
          </p>
          <ul className="space-y-sp-1">
            {services.map((svc) => (
              <li key={svc.name} className="flex items-center justify-between">
                <span className="inline-flex items-center gap-sp-2">
                  <span
                    aria-hidden
                    className={`inline-block h-sp-2 w-sp-2 rounded-r-chip ${DOT_CLASS[svc.status]}`}
                  />
                  <span className="text-fs-body text-gray-900">{svc.name}</span>
                </span>
                <span className="font-mono text-fs-meta text-gray-700">
                  {svc.latencyMs}ms
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
